/**
 * IngredientButton — nút nguyên liệu có selected state + click feedback.
 *
 * Phase 1 fix 4 nguyên liệu: coffee, milk, ice, sugar.
 *
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-06 §7.2 step 2.
 *   - `design/6. Thiết kế icon nguyên liệu Phase 1.png`.
 */
import { motion } from "framer-motion";

import type { IngredientCode } from "../types";

const INGREDIENT_LABEL: Record<IngredientCode, string> = {
  coffee: "Cà phê",
  milk: "Sữa",
  ice: "Đá",
  sugar: "Đường",
};

const INGREDIENT_ICON: Record<IngredientCode, string> = {
  coffee: "/assets/ingredients/coffee.png",
  milk: "/assets/ingredients/milk.png",
  ice: "/assets/ingredients/ice.png",
  sugar: "/assets/ingredients/sugar.png",
};

interface Props {
  code: IngredientCode;
  /** Số lần đã được chọn (≥ 0). Hiển thị badge nếu > 1. */
  selectedCount?: number;
  onClick: (code: IngredientCode) => void;
  disabled?: boolean;
}

export function IngredientButton({
  code,
  selectedCount = 0,
  onClick,
  disabled = false,
}: Props) {
  const isSelected = selectedCount > 0;

  return (
    <motion.button
      type="button"
      data-testid={`ingredient-${code}`}
      data-selected={isSelected ? "true" : "false"}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.92 }}
      onClick={() => onClick(code)}
      className={[
        "relative flex flex-col items-center justify-center gap-1 p-3 rounded-2xl",
        "border-2 transition-colors duration-150 select-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        isSelected
          ? "bg-amber50/20 border-amber50 shadow-soft-amber"
          : "bg-white border-coffee/15 hover:border-coffee/35",
      ].join(" ")}
      aria-pressed={isSelected}
      aria-label={INGREDIENT_LABEL[code]}
    >
      <img
        src={INGREDIENT_ICON[code]}
        alt=""
        aria-hidden="true"
        className="w-14 h-14 object-contain select-none pointer-events-none"
        draggable={false}
      />
      <span className="text-xs font-semibold text-coffee">
        {INGREDIENT_LABEL[code]}
      </span>
      {selectedCount > 1 && (
        <span
          data-testid={`ingredient-${code}-count`}
          className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 rounded-full bg-coffee text-white text-xs font-bold flex items-center justify-center px-1"
        >
          ×{selectedCount}
        </span>
      )}
    </motion.button>
  );
}
